if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this._loadRequestId = 0;
      this._abortController = null;
      this._hasLoadedOnce = false;
      this._intersectionObserver = null;

      this._onAjaxLoaded = this._onAjaxLoaded.bind(this);
    }

    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      document.addEventListener('product:ajax-loaded', this._onAjaxLoaded);

      this._intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0] || !entries[0].isIntersecting) return;
          if (this._intersectionObserver) {
            this._intersectionObserver.unobserve(this);
          }
          this.load();
        },
        { rootMargin: '0px 0px 200px 0px' }
      );
      this._intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      document.removeEventListener('product:ajax-loaded', this._onAjaxLoaded);
      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
        this._intersectionObserver = null;
      }
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
    }

    _onAjaxLoaded(event) {
      const productId = this._resolveProductId(event && event.detail);
      if (!productId) return;

      const updated = this.updateProductId(productId);
      // Always reload after sibling switch so empty complementary clears stale cards
      if (updated || this._hasLoadedOnce) {
        this.load({ force: true });
      }
    }

    _resolveProductId(detail) {
      if (detail && detail.productId) {
        return String(detail.productId);
      }

      const section =
        (detail && detail.section) ||
        document.querySelector('[data-main-product-section]');

      if (section) {
        const fromSection =
          section.getAttribute('data-product-id') ||
          (section.dataset && section.dataset.productId);
        if (fromSection) return String(fromSection);
      }

      const metaId =
        window.ShopifyAnalytics &&
        ShopifyAnalytics.meta &&
        ShopifyAnalytics.meta.product &&
        ShopifyAnalytics.meta.product.id;
      if (metaId) return String(metaId);

      return '';
    }

    updateProductId(productId) {
      if (!productId || !this.dataset.url) return false;

      try {
        const url = new URL(this.dataset.url, window.location.origin);
        const prev = url.searchParams.get('product_id');
        if (String(prev) === String(productId)) return false;
        url.searchParams.set('product_id', String(productId));
        // Keep relative/absolute consistent with original attribute style
        this.dataset.url = url.pathname + url.search;
        return true;
      } catch (e) {
        const next = this.dataset.url.replace(
          /([?&])product_id=\d+/i,
          `$1product_id=${encodeURIComponent(productId)}`
        );
        if (next === this.dataset.url) return false;
        this.dataset.url = next;
        return true;
      }
    }

    load(options = {}) {
      const force = Boolean(options.force);
      if (!this.dataset.url) return Promise.resolve();

      if (this._intersectionObserver) {
        this._intersectionObserver.unobserve(this);
      }

      if (this._abortController) {
        this._abortController.abort();
      }

      const requestId = ++this._loadRequestId;
      const controller =
        typeof AbortController === 'function' ? new AbortController() : null;
      this._abortController = controller;

      const fetchOptions = controller ? { signal: controller.signal } : {};

      return fetch(this.dataset.url, fetchOptions)
        .then((response) => {
          if (!response.ok) throw new Error(`Recommendations fetch failed: ${response.status}`);
          return response.text();
        })
        .then((text) => {
          if (requestId !== this._loadRequestId) return;

          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');
          const hasItems = Boolean(
            html.querySelector('.grid__item') ||
              html.querySelector('.product-reco-scroll__item') ||
              html.querySelector('.product-reco-scroll > li')
          );

          if (recommendations && recommendations.innerHTML.trim().length && hasItems) {
            this.innerHTML = recommendations.innerHTML;
            this.classList.add('product-recommendations--loaded');
            this.removeAttribute('hidden');
          } else if (force || this._hasLoadedOnce) {
            // Clear stale recommendations when the new product has none
            this.innerHTML = '';
            this.classList.remove('product-recommendations--loaded');
            this.setAttribute('hidden', '');
          }

          this._hasLoadedOnce = true;

          if (window.Currency && typeof Currency.convertAll === 'function') {
            const cookieCurrency =
              Currency.cookie && typeof Currency.cookie.read === 'function'
                ? Currency.cookie.read()
                : null;
            if (cookieCurrency) {
              Currency.convertAll(
                window.shop_currency,
                cookieCurrency,
                'product-recommendations span.money',
                'money_format'
              );
            }
          }
        })
        .catch((e) => {
          if (e && e.name === 'AbortError') return;
          console.error(e);
        });
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);
}
